import { Injectable, OnDestroy } from '@angular/core';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

/**
 * MatPaginatorIntl implementation backed by ngx-translate.
 *
 * All visible paginator strings ("Items per page", range label, and the
 * first/previous/next/last button tooltips) are resolved from the project's
 * i18n files under the top-level `PAGINATOR` key. Labels are refreshed on every
 * language change so the paginator follows the active app language.
 *
 * Registered globally as the MatPaginatorIntl provider in AppModule, so it
 * applies to every <mat-paginator> in the app without per-component wiring.
 */
@Injectable()
export class TranslatedPaginatorIntl extends MatPaginatorIntl implements OnDestroy {
  private langChangeSub: Subscription;

  constructor(private translate: TranslateService) {
    super();

    // Re-translate whenever the active language changes.
    this.langChangeSub = this.translate.onLangChange.subscribe(() => {
      this.applyTranslations();
    });

    this.applyTranslations();
  }

  private applyTranslations(): void {
    this.itemsPerPageLabel = this.translate.instant('PAGINATOR.ITEMS_PER_PAGE');
    this.nextPageLabel = this.translate.instant('PAGINATOR.NEXT_PAGE');
    this.previousPageLabel = this.translate.instant('PAGINATOR.PREVIOUS_PAGE');
    this.firstPageLabel = this.translate.instant('PAGINATOR.FIRST_PAGE');
    this.lastPageLabel = this.translate.instant('PAGINATOR.LAST_PAGE');

    // Notify any live paginators to re-render their labels.
    this.changes.next();
  }

  override getRangeLabel = (page: number, pageSize: number, length: number): string => {
    if (length === 0 || pageSize === 0) {
      return this.translate.instant('PAGINATOR.RANGE_ZERO', { length });
    }

    const total = Math.max(length, 0);
    const start = page * pageSize;
    // Guard against a page index that lands past the end of the data.
    const end = start < total
      ? Math.min(start + pageSize, total)
      : start + pageSize;

    return this.translate.instant('PAGINATOR.RANGE', {
      start: start + 1,
      end,
      length: total
    });
  };

  ngOnDestroy(): void {
    this.langChangeSub?.unsubscribe();
  }
}
